import { useLayoutEffect, useState, useEffect } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Image from "../../components/images/Image";
import Shimmer from "../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../services/useApi";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import config from "../../config/config";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getFirefoxCssFix } from "../../components/commonfunctions/getFirefoxCssFix";
import Paginate from "../../components/tables/Paginate";
import { formatDate } from "../../components/commonfunctions/convertDate";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { showToast } from "../../components/toastMessage/toastSlice";
import { useDispatch } from "react-redux";
import ToolTip from "../../components/ToolTip/ToolTip";


// Define interfaces for type safety
interface CompanyDetails {
    company_name?: string;
    company_code?: string;
    country_code?: string;
    contact_person_name?: string;
    representative_profession?: string;
    contact_email?: string;
    contact_phone_number?: string;
    business_type?: string;
    office_no?: string;
    street_name?: string;
    market_block_number?: string;
    floor_number?: string;
    district?: string;
    city_town?: string;
    region?: string;
    country?: string;
    postal_code?: string;
    company_license_number?: string;
    company_license_expiry_date?: string;
    company_tin?: string;
    contract_start_date?: string;
    contract_end_date?: string;
    shareholder_nic_front?: string;
    shareholder_nic_back?: string;
    company_license_front?: string;
    company_license_back?: string;
    [key: string]: any; // For any other properties
}

interface CorporateClient {
    user_code: string;
    user_name: string;
}

export default function ViewCompanyDetails() {
    const dispatch = useDispatch();
    const { getAPI } = useApi();
    const { notEnglish } = useTranslation(adminContents);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const pathname = useSelector(
        (state: RootState) => state.geolocation.previousUrl
    );
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [companyDetails, setCompanyDetails] = useState<CompanyDetails>({});
    const [currentPage, setCurrentPage] = useState(() => {
        const page = searchParams.get("page");
        return page ? parseInt(page) : 1;
    });
    const [itemsPerPage] = useState(10);
    const [corporateClients, setCorporateClients] = useState<CorporateClient[]>([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [addClicked, setAddClicked] = useState(false);
    
    // Update URL when page changes
    useEffect(() => {
        setSearchParams({ page: currentPage.toString() });
    }, [currentPage, setSearchParams]);

    /**
     * This function fetches details of a master distributor from an API and updates the state accordingly,
     * handling errors and navigation based on the response.
     */
    const fetchCompanyDetails = async () => {
        setLoading(true);
        try {
            const response = await getAPI(
                `companies/${id}`
            );
            if (!response?.error) {
                const temp = response?.data?.data as CompanyDetails;
                setCompanyDetails(temp);
            } else {
            dispatch(showToast({
                message: response.data?.response?.data?.message || "Error fetching corporate clients",
                type: "error"
            }));
            }
        } catch (error) {
            console.error("Error fetching admin details:", error);
        }
        setLoading(false);
    };
    
    /**
     * This function fetches corporate clients for the company from an API and updates the state accordingly.
     */
    const fetchCorporateClients = async () => {
        setClientsLoading(true);
        try {
            const response = await getAPI(
                `companies/${id}/corporate-clients?page_number=${currentPage}`
            );
            if (!response?.error) {
                // Map API response to match the expected structure
                const clients = (response?.data?.data || []).map((client: any) => ({
                    user_code: client.user_code,
                    user_name: client.name // API returns 'name' but we use 'user_name' in our component
                }));
                setCorporateClients(clients);
                setTotalCount(parseInt(response?.data?.total_count || "0"));
            } else {
                dispatch(showToast({
                                message: response.data?.response?.data?.message || "Error fetching corporate clients",
                                type: "error"
                            }));
                console.error("Error fetching corporate clients:", response?.data);
            }
        } catch (error) {
            console.error("Error fetching corporate clients:", error);
        }
        setClientsLoading(false);
    };

    useLayoutEffect(() => {
        fetchCompanyDetails();
        fetchCorporateClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const basicDetailsHeadings = [
        {
            id: "contact_person_name",
            title: "Contact Person/Representative Name ",
        },
        {
            id: "representative_profession",
            title: "Representative Profession",
        },
        {
            id: "contact_email",
            title: "Contact Email Address",
        },
        {
            id: "contact_phone_number",
            title: "Contact Phone Number",
        },
        {
            id: "business_type",
            title: "Business Type",
        },
    ];

    // Residential details are now directly accessed in the JSX
    const companyDetailsObject = [
        {
            id: "company_license_number",
            title: "Company Liscense Number",
        },
        {
            id: "company_license_expiry_date",
            title: "Company License Expiry Date",
        },
        {
            id: "company_tin",
            title: "Company TIN",
        },
        {
            id: "contract_start_date",
            title: "Contract Start Date",
        },
        {
            id: "contract_end_date",
            title: "Contract End Date",
        },
    ];

    const addressParts = [
        companyDetails?.office_no,
        companyDetails?.street_name,
        companyDetails?.market_block_number,
        companyDetails?.floor_number,
        companyDetails?.district,
        companyDetails?.city_town,
        companyDetails?.region,
        companyDetails?.country,
        companyDetails?.postal_code
    ];

    const addressString = addressParts
    .filter(part => part) // Remove null, undefined, and empty values
    .join(", ") || "-"; // Join with comma only for valid values
    return (
        <div dir={notEnglish ? "rtl" : "ltr"}>
            <Breadcrumbs
                navPath={[
                    "Company Management",
                    "List of Companies",
                    "View Company Details",
                ]}
                links={
                    pathname
                        ? [pathname, pathname]
                        : [
                            '/company-management',
                            '/company-management',
                        ]
                }
                heading={`View Company Details`}
            />
            {!loading ? (
                <div className="my-2 bg-white h-maintableheight rounded-lg flex flex-col">
                    <div className="px-6 py-5 border-b border-[#E5E9EB] flex flex-wrap justify-between items-start gap-4">
                        <div>
                            <div
                                title={companyDetails?.company_name || ""}
                                data-testid="name"
                                className="flex flex-wrap items-start gap-4 text-heading2"
                            >
                                <div
                                    data-testid="user-name"
                                    className="break-all"
                                >
                                    {companyDetails?.company_name || "-"}
                                </div>
                            </div>
                            <p
                                title={companyDetails?.company_code || ""}
                                data-testid="user-code"
                                className="text-subTitle"
                            >
                                {companyDetails?.company_code || "-"}
                            </p>
                        </div>
                        <div className="flex gap-[24px] print:hidden shrink-0">
                            <Image
                                testId="edit-btn"
                                src="editImg"
                                key="edit"
                                className="z-50 w-[24px] mt-[6px] h-[24px] cursor-pointer"
                                onClick={() => {
                                    navigate(`/company-management/view-company/edit-company/${id}`);
                                }}
                            />
                        </div>
                    </div>
                    <div
                        className={`flex-grow px-6 mt-5 ${getFirefoxCssFix(
                            "scroll"
                        )} overflow-auto`}
                    >
                        <div className=" border-gray-200">
                            <h1 className="text-heading3 underline underline-offset-4 decoration-gray-400">
                                Basic Details
                            </h1>

                            <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {basicDetailsHeadings.map((item, index) => (
                                    <div key={index} className="mb-4">
                                        <h1 className="text-sm text-gray-600">{item?.title}</h1>
                                        <p
                                            data-testid={item?.id}
                                            className="mt-1 font-medium text-gray-900 break-all"
                                        >
                                            {item.id === "contact_phone_number" && companyDetails?.contact_phone_number && companyDetails?.country_code
                                                ? `+${companyDetails.country_code} ${companyDetails[item.id].replace(companyDetails.country_code, "")}`
                                                : companyDetails[item.id] || "-"}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div>
                                    <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">
                                        Residential Details
                                    </h1>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Address</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="address">
                                            {addressString}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div>
                                    <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">
                                        Company Details
                                    </h1>
                                    <div className="mt-6">
                                        <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {companyDetailsObject.map((item, index) => (
                                                <div key={index} className="mb-4">
                                                    <h1 className="text-sm text-gray-600">
                                                        {item?.title}
                                                    </h1>
                                                    <p
                                                        data-testid={item?.id}
                                                        className="mt-1 font-medium text-gray-900 break-all"
                                                    >{
                                                            item.id.includes('date') && companyDetails?.[item.id] 
                                                            ? formatDate(companyDetails[item.id]) 
                                                            : companyDetails[item.id] || "-"
                                                        }
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div className="mb-4 group relative">
                                            <h1 className="text-sm text-gray-600">
                                                Share Holder NIC Front Page
                                            </h1>
                                            {companyDetails?.shareholder_nic_front ? <div
                                                className="w-full sm:w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                                            >
                                                <p className="more-lines">{companyDetails?.shareholder_nic_front.split("/").pop()}</p>
                                                <ToolTip ToolTipItem={companyDetails?.shareholder_nic_front.split("/").pop() || ''} top="top-0" />
                                                <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                                    const fileUrl = config?.cdnURL + 'public/' + companyDetails?.shareholder_nic_front;
                                                    window.open(fileUrl, "_blank");
                                                }} />
                                            </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                                        </div>
                                        <div className="mb-4 group relative">
                                            <h1 className="text-sm text-gray-600">
                                                Share Holder NIC Back Page
                                            </h1>
                                            {companyDetails?.shareholder_nic_back ? <div
                                                className="w-full sm:w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                                            >
                                                <p className="more-lines">{companyDetails?.shareholder_nic_back.split("/").pop()}</p>
                                                <ToolTip ToolTipItem={companyDetails?.shareholder_nic_back.split("/").pop() || ''} top="top-0" />
                                                <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                                    const fileUrl = config?.cdnURL + 'public/' + companyDetails?.shareholder_nic_back;
                                                    window.open(fileUrl, "_blank");
                                                }} />
                                            </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                                        </div>
                                    </div>
                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div className="mb-4 group relative">
                                            <h1 className="text-sm text-gray-600">
                                            Company License Front Page
                                            </h1>
                                            {companyDetails?.company_license_front ? <div
                                                className="w-full sm:w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                                            >
                                                <p className="more-lines">{companyDetails?.company_license_front.split("/").pop()}</p>
                                                <ToolTip ToolTipItem={companyDetails?.company_license_front.split("/").pop() || ''} top="top-0" />
                                                <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                                    const fileUrl = config?.cdnURL + 'public/' + companyDetails?.company_license_front;
                                                    window.open(fileUrl, "_blank");
                                                }} />
                                            </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                                        </div>
                                        <div className="group relative">
                                            <h1 className="text-sm text-gray-600">
                                            Company License Back Page
                                            </h1>
                                            {companyDetails?.company_license_back ? <div
                                                className="w-full sm:w-[68%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1"
                                            >
                                                <p className="more-lines">{companyDetails?.company_license_back.split("/").pop()}</p>
                                                <ToolTip ToolTipItem={companyDetails?.company_license_back.split("/").pop() || ''} top="top-0" />
                                                <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                                    const fileUrl = config?.cdnURL + 'public/' + companyDetails?.company_license_back;
                                                    window.open(fileUrl, "_blank");
                                                }} />
                                            </div> : <p className="mt-1 font-medium text-gray-900">-</p>}
                                        </div>
                                    </div>

                                </div>
                            </div>
                            
                            {clientsLoading || corporateClients.length > 0 ? (
                                <div>
                                    <div>
                                        <h1 className="font-semibold text-body1 underline decoration-2 underline-offset-4 mt-6 decoration-gray-400">
                                            Corporate Clients
                                        </h1>
                                        <div className="mt-6 pb-3.5">
                                            <div className="overflow-x-auto">
                                                {clientsLoading ? (
                                                    <div className="py-4">
                                                        <div className="animate-pulse space-y-4">
                                                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                                                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                                                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                                                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                                                            <div className="h-10 bg-gray-200 rounded w-full"></div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="min-w-full border border-[#D8DFE9]" data-testid="corporate-clients-table">
                                                            <div className="bg-p_light" data-testid="table-header">
                                                                <div className="flex" data-testid="table-row">
                                                                    <div className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]" data-testid="table-header-cell">
                                                                        User Code
                                                                    </div>
                                                                    <div className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]" data-testid="table-header-cell">
                                                                        User Name
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="bg-white" data-testid="table-body">
                                                                {corporateClients.map((client: CorporateClient, index: number) => (
                                                                        <div key={index} className="flex" data-testid="table-row">
                                                                            <div className="px-6 py-2.5 text-sm font-medium text-primary_text border border-[#D8DFE9] break-all" data-testid="table-cell">
                                                                                {client.user_code}
                                                                            </div>
                                                                            <div className="px-6 py-2.5 text-sm font-medium text-primary_text border border-[#D8DFE9] break-all" data-testid="table-cell">
                                                                                {client.user_name}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                        {totalCount > itemsPerPage && (
                                                            <div className="mt-4" data-testid="pagination">
                                                                <Paginate
                                                                    totalPages={Math.ceil(totalCount / itemsPerPage)}
                                                                    currentPage={currentPage}
                                                                    onPageChange={setCurrentPage}
                                                                    loading={clientsLoading}
                                                                    displayTotalRecords={false}
                                                                    title="Corporate Clients"
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : (
                <Shimmer />
            )}
        </div>
    );
}